import React from 'react'
import styled from 'styled-components'
import { StyledModal, colors, GrayButton } from '../theme/sharedComponents'
import StyledAlert from './StyledAlert'
import WshTourButton from '@wsh/WshTourButton'
import { IS_DEMO } from '../constants/env'

// ─── Shared Tab Components ────────────────────────────────────────────────────
// Used by any modal that needs tabs. Import these instead of defining locally.

export const ModalTabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  border-bottom: 2px solid ${colors.borderLight};
  margin-bottom: 1.5rem;
`

export const ModalTab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? colors.primary : 'transparent')};
  margin-bottom: -2px;
  padding: 0.6rem 1.1rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${(p) => (p.$active ? colors.primary : colors.textLightModeMuted)};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 0.15s;

  &:hover {
    color: ${colors.primary};
  }
`

// ─── AppModal ─────────────────────────────────────────────────────────────────
// For complex, scrollable modals (lg size by default).
// Usage:
//   <AppModal show={show} onHide={onClose} title="Order Receipt" icon="bi bi-receipt"
//             footer={<GrayButton onClick={onClose}>Close</GrayButton>}>
//     <ModalTabBar>...</ModalTabBar>
//     {content}
//   </AppModal>

interface AppModalProps {
  show: boolean
  onHide: () => void
  title: React.ReactNode
  icon?: string
  size?: 'sm' | 'lg' | 'xl'
  scrollable?: boolean
  backdrop?: boolean | 'static'
  footer?: React.ReactNode
  children: React.ReactNode
  error?: string | null
  loading?: boolean
  noDismiss?: boolean
  onTourClick?: () => void
  allowInteractionInDemo?: boolean
}

export const AppModal: React.FC<AppModalProps> = ({
  show,
  onHide,
  title,
  icon,
  size,
  scrollable = true,
  backdrop,
  footer,
  children,
  error,
  loading = false,
  noDismiss = false,
  onTourClick,
  allowInteractionInDemo = false,
}) => {
  const locked = loading || noDismiss
  return (
    <StyledModal
      show={show}
      onHide={onHide}
      size={size}
      centered
      scrollable={scrollable}
      backdrop={locked ? 'static' : backdrop}
      keyboard={!locked}
      {...(backdrop !== undefined && !locked && { backdrop })}
    >
      <StyledModal.Header closeButton={!locked}>
        <StyledModal.Title>
          {icon && <i className={`${icon} me-2`} />}
          {title}
        </StyledModal.Title>
      </StyledModal.Header>
      <StyledModal.Body>
        {error && <StyledAlert variant="danger" className="mb-3">{error}</StyledAlert>}
        <div style={IS_DEMO && !allowInteractionInDemo ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
          {children}
        </div>
      </StyledModal.Body>
      {onTourClick && <WshTourButton variant="modal" onClick={onTourClick} />}
      {footer !== undefined && (
        <StyledModal.Footer>
          {IS_DEMO && !allowInteractionInDemo ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: colors.textLightModeMuted }}>
                <i className="bi bi-lock me-1" />
                Read-only demo,{' '}
                <a href="https://computeranything.dev/contact?projectType=wholesale-hub-demo" target="_blank" rel="noopener noreferrer">
                  get your own instance →
                </a>
              </span>
              <GrayButton onClick={onHide}>Close</GrayButton>
            </div>
          ) : footer}
        </StyledModal.Footer>
      )}
    </StyledModal>
  )
}

